import { POLL_MS } from '../lib/polling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PosDataSource } from '../lib/posData/types';
import { useOnlineStatus } from '../lib/useOnlineStatus';

/**
 * The offline status chip (docs/offline/target-architecture.md, "Offline
 * state UX"; extended for Phase 4 sync). Rendered only in local mode; the
 * cashier is never asked to make a sync decision, and never sees a raw
 * technical error - the chip only ever says one of these, calmly:
 *
 *   quiet            - online, nothing waiting, nothing wrong
 *   "N to send"      - bills recorded locally, waiting to reach the server
 *   "Offline"        - no network; sales keep saving on this till
 *   "could not be sent" - some bills are stuck (a permanent rejection or a
 *                         conflict); tap retries a sync cycle
 *   "Sign in again"  - the session needs re-authenticating before sync can
 *                      resume; POS writes keep working locally regardless
 *
 * Never reports "quiet" while a known failure sits in the queue - that is
 * `getSyncStatus()`'s own job (FAILED/CONFLICT stay reported even once the
 * PENDING count hits zero), this component just renders what it is told.
 */
export function PosStatusChip({ source }: { source: PosDataSource }) {
  const online = useOnlineStatus();
  const queryClient = useQueryClient();

  const pending = useQuery({
    queryKey: ['pos-pending-sync'],
    queryFn: () => source.pendingSyncCount(),
    refetchInterval: POLL_MS,
  });
  const status = useQuery({
    queryKey: ['pos-sync-status'],
    queryFn: () => source.getSyncStatus(),
    refetchInterval: POLL_MS,
  });

  const syncNow = useMutation({
    // Tagged so SyncActivityIndicator can watch this exact mutation's
    // isPending state too, for zero-latency feedback on a manual tap
    // (docs comment in SyncActivityIndicator.tsx has the full rationale).
    mutationKey: ['pos-sync-now'],
    mutationFn: () => source.syncNow(),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pos-pending-sync'] });
      void queryClient.invalidateQueries({ queryKey: ['pos-sync-status'] });
    },
  });

  if (source.mode !== 'local') return null;
  const count = pending.data ?? 0;

  if (!online) {
    return (
      <span className="chip" title="No connection. Sales keep saving on this device.">
        ● Offline — sales saved on this device{count > 0 ? ` · ${count} to send` : ''}
      </span>
    );
  }

  // A cashier-actionable problem: tapping just asks the engine to try again
  // (the same thing the background timer already does) - never a decision
  // about WHAT to retry, since the engine itself decides that safely.
  if (status.data === 'AUTH_REQUIRED') {
    return (
      <button
        type="button"
        className="chip warn"
        style={{ border: 'none', font: 'inherit', cursor: syncNow.isPending ? 'default' : 'pointer' }}
        title="This device's session needs signing in again before it can send bills. Sales keep saving locally."
        onClick={() => syncNow.mutate()}
        disabled={syncNow.isPending}
      >
        ⚠ Sign in again to send bills{count > 0 ? ` · ${count} waiting` : ''}
      </button>
    );
  }
  if (status.data === 'FAILED' || status.data === 'CONFLICT') {
    return (
      <button
        type="button"
        className="chip warn"
        style={{ border: 'none', font: 'inherit', cursor: syncNow.isPending ? 'default' : 'pointer' }}
        title={
          status.data === 'CONFLICT'
            ? 'A bill needs review before it can finish sending. Tap to retry the rest.'
            : 'Some bills could not be sent and need attention. Tap to retry.'
        }
        onClick={() => syncNow.mutate()}
        disabled={syncNow.isPending}
      >
        ⚠ {status.data === 'CONFLICT' ? 'A bill needs review' : 'Some bills could not be sent'} — tap to retry
      </button>
    );
  }
  if (count > 0) {
    return (
      <span className="chip" title="Bills recorded on this device, waiting to be sent to the server.">
        {count} bill{count === 1 ? '' : 's'} to send
      </span>
    );
  }
  return null;
}
